"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Menu, Sun, Phone, Laptop, Tv, Battery, Wrench, Monitor, Folder } from "lucide-react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Menubar, MenubarContent, MenubarMenu, MenubarTrigger } from "@/components/ui/menubar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { slugify } from "@/lib/utils/format-slug";
import { cn } from "@/lib/utils";

interface CategoryColumn {
  title: string;
  items: string[];
}

interface CategoryData {
  name: string;
  icon: React.ElementType;
  columns: CategoryColumn[];
  brands: string[];
}

const categoriesData: CategoryData[] = [
  {
    name: "Solar & Energy",
    icon: Sun,
    columns: [
      {
        title: "SOLAR PANELS",
        items: [
          "Monocrystalline Panels",
          "Polycrystalline Panels",
          "Rooftop Panels",
          "Industrial Panels",
          "Portable Solar Panels",
          "Flexible Panels",
          "Bifacial Panels",
          "Complete Solar Kits",
        ],
      },
      {
        title: "ENERGY STORAGE",
        items: [
          "Lithium Batteries",
          "Lead-Acid Batteries",
          "Battery Banks",
          "Power Walls",
          "Battery Cabinets",
          "Battery Accessories",
          "Energy Management",
        ],
      },
      {
        title: "SYSTEM COMPONENTS",
        items: [
          "Charge Controllers",
          "Solar Cables",
          "MC4 Connectors",
          "Junction Boxes",
          "Combiner Boxes",
          "Mounting Systems",
          "Installation Tools",
        ],
      },
    ],
    brands: ["SunPower", "Canadian Solar", "Jinko Solar", "Luminous", "Su-Kam", "Victron Energy", "Felicity", "Huawei"],
  },
  {
    name: "Phones & Accessories",
    icon: Phone,
    columns: [
      {
        title: "SMARTPHONES",
        items: ["Android Phones", "iOS Phones", "Budget Phones", "Flagship Phones", "Refurbished Phones"],
      },
      {
        title: "ACCESSORIES",
        items: ["Cases & Covers", "Screen Protectors", "Chargers", "Cables", "Power Banks", "Headphones", "Earbuds"],
      },
      {
        title: "TABLETS",
        items: ["Android Tablets", "iOS Tablets", "Tablet Accessories", "Styluses"],
      },
    ],
    brands: ["Samsung", "Apple", "Xiaomi", "Oppo", "Vivo", "Tecno", "Infinix", "Realme"],
  },
  {
    name: "Computers",
    icon: Laptop,
    columns: [
      {
        title: "LAPTOPS",
        items: ["Gaming Laptops", "Business Laptops", "Ultrabooks", "Budget Laptops", "Workstations"],
      },
      {
        title: "DESKTOPS",
        items: ["Gaming PCs", "Office PCs", "All-in-One", "Mini PCs"],
      },
      {
        title: "COMPONENTS",
        items: ["Processors", "Graphics Cards", "RAM", "Storage", "Motherboards", "Power Supplies"],
      },
    ],
    brands: ["HP", "Dell", "Lenovo", "ASUS", "Acer", "MSI", "Apple", "Microsoft"],
  },
  {
    name: "Electronics",
    icon: Tv,
    columns: [
      {
        title: "HOME APPLIANCES",
        items: ["Televisions", "Refrigerators", "Washing Machines", "Air Conditioners", "Microwaves"],
      },
      {
        title: "AUDIO & VIDEO",
        items: ["Speakers", "Soundbars", "Home Theaters", "Projectors", "Cameras"],
      },
      {
        title: "SMART DEVICES",
        items: ["Smart TVs", "Smart Speakers", "Smart Home Hubs", "IoT Devices"],
      },
    ],
    brands: ["Samsung", "LG", "Sony", "Panasonic", "TCL", "Hisense", "Xiaomi", "Philips"],
  },
  {
    name: "Inverters & Batteries",
    icon: Battery,
    columns: [
      {
        title: "INVERTERS",
        items: ["Pure Sine Wave", "Modified Sine Wave", "Solar Inverters", "Hybrid Inverters", "Grid-Tie Inverters"],
      },
      {
        title: "BATTERIES",
        items: ["Lithium Batteries", "Lead-Acid", "Gel Batteries", "AGM Batteries", "Deep Cycle"],
      },
      {
        title: "ACCESSORIES",
        items: ["Battery Chargers", "Battery Monitors", "Cables & Connectors", "Fuses & Breakers"],
      },
    ],
    brands: ["Luminous", "Su-Kam", "Microtek", "Exide", "Amaron", "Victron", "Schneider", "APC"],
  },
  {
    name: "Installation & Tools",
    icon: Wrench,
    columns: [
      {
        title: "INSTALLATION TOOLS",
        items: ["Drills", "Screwdrivers", "Wrenches", "Crimping Tools", "Wire Strippers"],
      },
      {
        title: "MOUNTING SYSTEMS",
        items: ["Solar Mounts", "Rack Systems", "Ground Mounts", "Pole Mounts", "Tracking Systems"],
      },
      {
        title: "SAFETY EQUIPMENT",
        items: ["Safety Harnesses", "Gloves", "Safety Glasses", "Hard Hats", "First Aid Kits"],
      },
    ],
    brands: ["Bosch", "DeWalt", "Makita", "Stanley", "Craftsman", "Milwaukee", "Klein Tools"],
  },
  {
    name: "Monitoring Systems",
    icon: Monitor,
    columns: [
      {
        title: "MONITORING DEVICES",
        items: ["Energy Monitors", "Battery Monitors", "Solar Monitors", "Smart Meters"],
      },
      {
        title: "SOFTWARE & APPS",
        items: ["Monitoring Software", "Mobile Apps", "Cloud Platforms"],
      },
      {
        title: "SENSORS",
        items: ["Temperature Sensors", "Voltage Sensors", "Current Sensors", "Environmental Sensors"],
      },
    ],
    brands: ["Victron", "SolarEdge", "Enphase", "Fronius", "SMA", "Huawei", "Growatt"],
  },
  {
    name: "Other Categories",
    icon: Folder,
    columns: [
      {
        title: "MISC CATEGORIES",
        items: ["Cables & Accessories", "Connectors", "Adapters", "Extension Cords"],
      },
      {
        title: "SUPPLIES",
        items: ["Electrical Supplies", "Wiring", "Conduits", "Junction Boxes"],
      },
    ],
    brands: ["Generic", "Various Brands"],
  },
];

// const primaryCategories = categoriesData.map((cat) => cat.name);

export default function CategoriesMenubar() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoriesData[0].name);

  const selectedCategoryData = selectedCategory
    ? categoriesData.find((cat) => cat.name === selectedCategory) || categoriesData[0]
    : categoriesData[0];

  return (
    <div className='flex w-full items-center gap-0'>
      <Menubar className='h-auto w-full border-none bg-transparent p-0 shadow-none'>
        <MenubarMenu>
          <MenubarTrigger className='text-text-title-inv dark:text-text-title dark:focus:text-text-title focus:text-text-title-inv dark:data-[state=open]:text-text-title data-[state=open]:text-text-title-inv flex cursor-pointer items-center gap-2 px-2 py-2 focus:bg-transparent data-[state=open]:bg-transparent md:px-4'>
            All Categories
            <Menu className='h-4 w-4' />
          </MenubarTrigger>
          <MenubarContent
            className='max-w-screen p-0 lg:min-w-[800px]'
            onMouseEnter={() => {
              if (!selectedCategory) {
                setSelectedCategory(categoriesData[0].name);
              }
            }}
          >
            <div className='flex'>
              {/* Left Sidebar - Primary Categories */}
              <div className='bg-muted/30 border-r lg:w-64'>
                {categoriesData.map((category) => {
                  const Icon = category.icon;
                  const isSelected = selectedCategoryData?.name === category.name;
                  return (
                    <Link
                      key={category.name}
                      href={`/category/${slugify(category.name)}`}
                      onMouseEnter={() => setSelectedCategory(category.name)}
                      className={cn(
                        "flex w-full items-center gap-3 px-2 py-1.5 text-left transition-colors lg:px-4 lg:py-3",
                        isSelected
                          ? "text-link-primary bg-transparent"
                          : "hover:bg-muted text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Icon className='size-4 shrink-0 lg:size-5' />
                      <span className='text-sm font-medium'>{category.name}</span>
                    </Link>
                  );
                })}
              </div>

              {/* Right Panel - Subcategories and Brands */}
              {selectedCategoryData && (
                <div className='flex-1 p-2 lg:p-6'>
                  {/* Mobile Accordion View */}
                  <div className='lg:hidden'>
                    <Accordion type='single' collapsible className='w-full'>
                      {/* Subcategory Accordion Items */}
                      {selectedCategoryData.columns.map((column, idx) => (
                        <AccordionItem key={idx} value={`column-${idx}`}>
                          <AccordionTrigger className='text-muted-foreground py-3 text-xs font-medium tracking-[0.35px] uppercase'>
                            {column.title}
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className='flex flex-col gap-2'>
                              {column.items.map((item) => {
                                const slug = slugify(item);
                                return (
                                  <li key={item}>
                                    <Link
                                      href={`/category/${slug}`}
                                      className='text-foreground hover:text-primary text-sm transition-colors'
                                    >
                                      {item}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      ))}

                      {/* Brands Accordion Item */}
                      {selectedCategoryData.brands.length > 0 && (
                        <AccordionItem value='brands'>
                          <AccordionTrigger className='text-muted-foreground py-3 text-xs font-medium tracking-[0.35px] uppercase'>
                            TOP BRANDS
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className='flex flex-col gap-2'>
                              {selectedCategoryData.brands.map((brand) => {
                                const slug = slugify(brand);
                                return (
                                  <li key={brand}>
                                    <Link
                                      href={`/brand/${slug}`}
                                      className='text-foreground hover:text-primary text-sm transition-colors'
                                    >
                                      {brand}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      )}
                    </Accordion>
                  </div>

                  {/* Desktop Grid View */}
                  <div
                    className='hidden gap-2 lg:grid lg:gap-6'
                    style={{
                      gridTemplateColumns: `repeat(${selectedCategoryData.columns.length + (selectedCategoryData.brands.length > 0 ? 1 : 0)}, minmax(0, 1fr))`,
                    }}
                  >
                    {/* Subcategory Columns */}
                    {selectedCategoryData.columns.map((column, idx) => (
                      <div key={idx} className='flex flex-col gap-2 lg:gap-3'>
                        <h3 className='text-muted-foreground text-sm font-medium tracking-[0.35px] uppercase'>
                          {column.title}
                        </h3>
                        <ul className='flex flex-col gap-2'>
                          {column.items.map((item) => {
                            const slug = slugify(item);
                            return (
                              <li key={item}>
                                <Link
                                  href={`/category/${slug}`}
                                  className='text-foreground hover:text-primary text-sm transition-colors'
                                >
                                  {item}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}

                    {/* Brands Column */}
                    {selectedCategoryData.brands.length > 0 && (
                      <div className='flex flex-col gap-2 lg:gap-3'>
                        <h3 className='text-muted-foreground text-sm font-medium tracking-[0.35px] uppercase'>
                          TOP BRANDS
                        </h3>
                        <ul className='flex flex-col gap-2'>
                          {selectedCategoryData.brands.map((brand) => {
                            const slug = slugify(brand);
                            return (
                              <li key={brand}>
                                <Link
                                  href={`/brand/${slug}`}
                                  className='text-foreground hover:text-primary text-sm transition-colors'
                                >
                                  {brand}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </MenubarContent>
        </MenubarMenu>
        <div className='scrollbar-hide min-w-0 flex-1 overflow-x-auto'>
          <div className='flex items-center gap-1 px-4 py-2 whitespace-nowrap sm:gap-2 md:gap-4'>
            {categoriesData.map((category) => {
              const slug = slugify(category.name);
              return (
                <MenubarMenu key={category.name}>
                  <MenubarTrigger className='text-text-title-inv dark:text-text-title dark:focus:text-text-title focus:text-text-title-inv dark:data-[state=open]:text-text-title data-[state=open]:text-text-title-inv data-[state=open]:bg-link-primary flex cursor-pointer items-center gap-2 rounded-full border-[0.5px] border-white/20 px-4 py-2 focus:bg-transparent md:border-none md:data-[state=open]:bg-transparent'>
                    <Link href={`/category/${slug}`} className='dark:text-text-title text-text-title-inv text-sm'>
                      {category.name}
                    </Link>
                    {/* <XMarkIcon className='text-text-title-inv size-4' /> */}
                    <ChevronDownIcon className='text-text-title-inv dark:text-text-title size-4' />
                  </MenubarTrigger>
                  <MenubarContent className='max-w-screen'>
                    <div className='flex-1 p-2 lg:p-6'>
                      {/* Mobile Accordion View */}
                      <div className='lg:hidden'>
                        <Accordion type='single' collapsible className='w-full'>
                          {/* Subcategory Accordion Items */}
                          {category.columns.map((column, idx) => (
                            <AccordionItem key={idx} value={`column-${idx}`}>
                              <AccordionTrigger className='text-muted-foreground py-3 text-xs font-medium tracking-[0.35px] uppercase'>
                                {column.title}
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className='flex flex-col gap-2'>
                                  {column.items.map((item) => {
                                    const slug = slugify(item);
                                    return (
                                      <li key={item}>
                                        <Link
                                          href={`/category/${slug}`}
                                          className='text-foreground hover:text-primary text-sm transition-colors'
                                        >
                                          {item}
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          ))}

                          {/* Brands Accordion Item */}
                          {category.brands.length > 0 && (
                            <AccordionItem value='brands'>
                              <AccordionTrigger className='text-muted-foreground py-3 text-xs font-medium tracking-[0.35px] uppercase'>
                                TOP BRANDS
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className='flex flex-col gap-2'>
                                  {category.brands.map((brand) => {
                                    const slug = slugify(brand);
                                    return (
                                      <li key={brand}>
                                        <Link
                                          href={`/brand/${slug}`}
                                          className='text-foreground hover:text-primary text-sm transition-colors'
                                        >
                                          {brand}
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          )}
                        </Accordion>
                      </div>

                      {/* Desktop Grid View */}
                      <div
                        className='hidden gap-2 lg:grid lg:gap-6'
                        style={{
                          gridTemplateColumns: `repeat(${category.columns.length + (category.brands.length > 0 ? 1 : 0)}, minmax(0, 1fr))`,
                        }}
                      >
                        {/* Subcategory Columns */}
                        {category.columns.map((column, idx) => (
                          <div key={idx} className='flex flex-col gap-2 lg:gap-3'>
                            <h3 className='text-muted-foreground text-sm font-medium tracking-[0.35px] uppercase'>
                              {column.title}
                            </h3>
                            <ul className='flex flex-col gap-2'>
                              {column.items.map((item) => {
                                const slug = slugify(item);
                                return (
                                  <li key={item}>
                                    <Link
                                      href={`/category/${slug}`}
                                      className='text-foreground hover:text-primary text-sm transition-colors'
                                    >
                                      {item}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}

                        {/* Brands Column */}
                        {category.brands.length > 0 && (
                          <div className='flex flex-col gap-2 lg:gap-3'>
                            <h3 className='text-muted-foreground text-sm font-medium tracking-[0.35px] uppercase'>
                              TOP BRANDS
                            </h3>
                            <ul className='flex flex-col gap-2'>
                              {category.brands.map((brand) => {
                                const slug = slugify(brand);
                                return (
                                  <li key={brand}>
                                    <Link
                                      href={`/brand/${slug}`}
                                      className='text-foreground hover:text-primary text-sm transition-colors'
                                    >
                                      {brand}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </MenubarContent>
                </MenubarMenu>
              );
            })}
          </div>
        </div>
      </Menubar>
    </div>
  );
}
